import React, {useContext, useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {
  LegendBoxGreen,
  LegendBoxRed,
  LegendWrapper,
  OffersWrapper
} from 'components/AvailabilityDatePicker/AvailabilityHelpers'
import {OffersList} from 'components/AvailabilityDatePicker/OffersList'
import {
  AvailabilityComponent,
  useAvailabilityDatePicker
} from 'components/AvailabilityDatePicker/useAvailability'
import {MinimalHotelCard} from 'components/MinimalHotelCard'
import {
  DatesTypes,
  SoldOutBodyMessage
} from 'components/SearchUnavailability/SoldOut/SoldOutBodyMessage'
import {TaxationAndPriceMessage} from 'components/TaxationAndPriceMessage'
import {SoldOutBase} from 'components/Unavailability/SoldOut/SoldOutBase'
import {UnavailabilityWrapper} from 'components/Unavailability/UnavailabilityComponent/UnavailabilityWithPopupTrigger'
import {
  getAnchorHotel,
  getAnchorHotelId,
  getHotelAvailabilityPrices,
  getHotelAvailabilityPricesComplete,
  getSearchParameters,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getSelectedAvailabilityDate} from 'modules/search/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'

import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {ConfigureI18nContext} from '@vio/core/src/localization/components/ConfigureI18n'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {AvailabilityDatePicker} from '../../../../shared/src/search/DatePicker/AvailabilityDatePicker'

const Container = styled.div`
  padding-bottom: ${cssTheme.layout.spacing.s500};
  display: grid;
  grid-template-columns: auto 350px;
  grid-column-gap: ${cssTheme.layout.spacing.s900};
`

const LeftSide = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: ${cssTheme.layout.spacing.s600};
  padding-right: ${cssTheme.layout.spacing.s600};
  gap: ${cssTheme.layout.spacing.s500};
`

const Legend = styled.div`
  display: flex;
  padding-top: ${cssTheme.layout.spacing.s300};
  padding-bottom: ${cssTheme.layout.spacing.s300};
`

const RightSide = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${cssTheme.layout.spacing.s600};
  padding-right: ${cssTheme.layout.spacing.s600};
  padding-bottom: ${cssTheme.layout.spacing.s500};
  padding-left: 0;
  gap: ${cssTheme.layout.spacing.s500};
`

const HotelCardWrapper = styled.div`
  max-height: 66px;
`

export const AvailabilityPopupContent = () => {
  const [initialSearchComplete, setInitialSearchComplete] =
    useState<boolean>(false)

  const hotel = useSelector(getAnchorHotel)
  const anchorHotelId = useSelector(getAnchorHotelId)
  const {rooms} = useSelector(getSearchBoxValues) || {}

  const {imageURIs, hotelName} = hotel || {}
  const imageUrl = imageURIs[0] || ''

  const {monthNames, weekDayNamesShort, firstDayOfWeek} =
    useContext(ConfigureI18nContext)

  const selectedAvailabilityDate = useSelector(getSelectedAvailabilityDate)
  const hotelAvailabilityPrices = useSelector(state =>
    getHotelAvailabilityPrices(state, anchorHotelId)
  )
  const {checkIn} = useSelector(getSearchParameters)

  const showTotalPrices = useSelector(getShowTotalPrices)
  const searchComplete = useSelector(getHotelAvailabilityPricesComplete)

  const selectedDateOffers =
    hotelAvailabilityPrices &&
    hotelAvailabilityPrices[selectedAvailabilityDate]?.offers

  const component = AvailabilityComponent.DesktopAvailabilityCalendar

  const {handleDayClick, handleMonthChange} = useAvailabilityDatePicker({
    component
  })

  useEffect(() => {
    if (searchComplete) {
      setInitialSearchComplete(true)
    }
  }, [searchComplete])

  return (
    <Container>
      <LeftSide>
        <AvailabilityDatePicker
          numberOfMonthsToShow={1}
          firstDayOfWeek={firstDayOfWeek}
          weekdaysShort={weekDayNamesShort}
          showWeekDays
          months={monthNames}
          selectedDate={selectedAvailabilityDate}
          hotelAvailabilityPrices={hotelAvailabilityPrices}
          checkIn={checkIn}
          onMonthChange={handleMonthChange}
          onDayClick={handleDayClick}
          showTotalPrices={showTotalPrices}
          searchComplete={searchComplete}
        />
        <Legend>
          <Text variant="bodyXS" colorPath="content.neutral.secondary">
            <TaxationAndPriceMessage showCurrencyCode hotelId={anchorHotelId} />
          </Text>
          <LegendWrapper>
            <LegendBoxGreen />
            <Text variant="bodyXS" colorPath="content.neutral.secondary">
              <FormattedMessageWrapper id="cheaper" defaultMessage="Cheaper" />
            </Text>
            <LegendBoxRed />
            <Text variant="bodyXS" colorPath="content.neutral.secondary">
              <FormattedMessageWrapper id="higher" defaultMessage="Higher" />
            </Text>
          </LegendWrapper>
        </Legend>
      </LeftSide>
      <RightSide>
        <HotelCardWrapper>
          <MinimalHotelCard
            title={hotelName}
            imageUrl={imageUrl}
            fullWidth
            rooms={rooms}
            isCompactImage
          />
        </HotelCardWrapper>

        {!selectedDateOffers && selectedAvailabilityDate && searchComplete && (
          <UnavailabilityWrapper
            backgroundColorPath="background.neutral.disabled"
            borderRadius="lg"
          >
            <SoldOutBase
              headerSlot={
                <SoldOutBodyMessage datesType={DatesTypes.Availability} />
              }
              contentSlot={
                <FormattedMessageWrapper
                  id="unavailability.selectedDatesNotAvailable"
                  defaultMessage="Your selected dates are not available. Check out other dates on the calendar."
                />
              }
            />
          </UnavailabilityWrapper>
        )}
        {selectedDateOffers && initialSearchComplete && (
          <OffersWrapper className="--full-width">
            <OffersList
              hotelId={anchorHotelId}
              offers={selectedDateOffers}
              sourceComponent={component}
            />
          </OffersWrapper>
        )}
      </RightSide>
    </Container>
  )
}
